'use client';

import { createGraphiQLFetcher } from '@graphiql/toolkit';
import { GraphiQL } from 'graphiql';
import React from 'react';

import config from 'configs/app';
import buildUrl from 'lib/api/buildUrl';
import isBrowser from 'lib/isBrowser';

import 'graphiql/graphiql.css';
import styles from './GraphiQLPage.module.css';

const feature = config.features.graphqlApiDocs;

const GraphiQLPage: React.FC = () => {
  const [ theme, setTheme ] = React.useState<string>('dark');

  React.useEffect(() => {
    if (isBrowser()) {
      const storedTheme = window.localStorage.getItem('graphiql:theme') ||
                          document.documentElement.getAttribute('data-theme') ||
                          'dark';
      setTheme(storedTheme);
      window.localStorage.setItem('graphiql:theme', storedTheme);

      // Listen for theme changes
      const observer = new MutationObserver(() => {
        const currentTheme = document.documentElement.getAttribute('data-theme') || 'dark';
        setTheme(currentTheme);
        window.localStorage.setItem('graphiql:theme', currentTheme);
      });

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: [ 'data-theme' ],
      });

      return () => observer.disconnect();
    }
  }, []);

  if (!feature.isEnabled) {
    return (
      <div className={ styles.container }>
        <div className={ styles.header }>
          <h1 className={ styles.title }>GraphQL Playground</h1>
          <p className={ styles.subtitle }>GraphQL API is not enabled</p>
        </div>
      </div>
    );
  }

  const initialQuery = `{
    transaction(
      hash: "${ feature.defaultTxHash }"
    ) {
      hash
      blockNumber
      value
      gasUsed
    }
  }`;

  const graphqlUrl = buildUrl('graphql');

  const fetcher = React.useMemo(() => {
    if (!isBrowser()) return null;
    return createGraphiQLFetcher({ url: graphqlUrl });
  }, [ graphqlUrl ]);

  if (!fetcher) {
    return null;
  }

  return (
    <div className={ styles.graphiqlContainer }>
      <div className={ styles.graphiqlWrapper }>
        <GraphiQL fetcher={ fetcher } defaultQuery={ initialQuery } key={ theme }/>
      </div>
    </div>
  );
};

export default GraphiQLPage;
